import { personDisplayName } from "./personName";
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableRow, TableCell, TableBody,
  TableFooter, TextField, Select, MenuItem, Checkbox, FormControlLabel, IconButton
} from "@mui/material";
import dayjs from "dayjs";
import Close from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import Save from "@mui/icons-material/Save";
import { red } from "@mui/material/colors";
import DialogDateField from "./DialogDateField";
import { normalizeDialogDate } from "./dialogDate";
import LoadingIndicator from './LoadingIndicator';

const EMPTY_ROW = {
  kunde_id: "",
  menge: "",
  einheit: "",
  preis: "",
  betrag: "",
  bezahlt: false,
  bemerkung: ""
};

const valueOrEmpty = (value) => (
  value === null || value === undefined || (typeof value === "string" && value.trim() === "")
    ? ""
    : value
);

const parseDecimal = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().replace(",", ".");
  const match = normalized.match(/^([+-]?)(\d*)(?:\.(\d*))?$/);
  if (!match || (!match[2] && !match[3])) return null;

  const fraction = match[3] || "";
  const digits = `${match[2] || "0"}${fraction}`.replace(/^0+(?=\d)/, "");
  return {
    value: window.BigInt(`${match[1]}${digits || "0"}`),
    scale: fraction.length
  };
};

const roundToCents = (value, scale) => {
  const negative = value < 0n;
  const absoluteValue = negative ? -value : value;
  let cents;

  if (scale > 2) {
    const divisor = 10n ** window.BigInt(scale - 2);
    const wholeCents = absoluteValue / divisor;
    const remainder = absoluteValue % divisor;
    cents = wholeCents + (remainder * 2n >= divisor ? 1n : 0n);
  } else {
    cents = absoluteValue * (10n ** window.BigInt(2 - scale));
  }

  return negative ? -cents : cents;
};

const formatCents = (cents, decimalSeparator) => {
  const negative = cents < 0n;
  const absoluteCents = negative ? -cents : cents;
  const euros = absoluteCents / 100n;
  const centPart = String(absoluteCents % 100n).padStart(2, "0");
  return `${negative && absoluteCents !== 0n ? "-" : ""}${euros}${decimalSeparator}${centPart}`;
};

export const calculateVerkaufBetrag = (menge, preis) => {
  const parsedMenge = parseDecimal(menge);
  const parsedPreis = parseDecimal(preis);
  if (!parsedMenge || !parsedPreis) return "";

  const product = parsedMenge.value * parsedPreis.value;
  const productScale = parsedMenge.scale + parsedPreis.scale;
  return formatCents(roundToCents(product, productScale), ".");
};

export const formatVerkaufBetrag = (betrag) => {
  const parsedBetrag = parseDecimal(betrag);
  if (!parsedBetrag) return "";
  return formatCents(roundToCents(parsedBetrag.value, parsedBetrag.scale), ",");
};

export const normalizeVerkaufDatum = (datum) => {
  if (datum === null || datum === undefined || String(datum).trim() === "") return "";
  const parsedDatum = dayjs(datum);
  return parsedDatum.isValid() ? parsedDatum.format("YYYY-MM-DD") : "";
};

export const getInitialVerkaufDatum = (initialVerkaeufe, defaultDatum) => {
  if (initialVerkaeufe?.length > 0) {
    return normalizeVerkaufDatum(initialVerkaeufe[0]?.datum);
  }
  return normalizeVerkaufDatum(defaultDatum);
};

export const createNewVerkaufRow = (wildDefaults, defaultDatum = "") => ({
  ...EMPTY_ROW,
  datum: normalizeVerkaufDatum(defaultDatum),
  einheit: valueOrEmpty(wildDefaults?.Std_Einheit),
  preis: valueOrEmpty(wildDefaults?.Std_Preis)
});

const isPersisted = (row) => row.id !== undefined && row.id !== null && row.id !== "";
const prepareInitialRows = (rows) => (rows || []).map(row => (
  isPersisted(row)
    ? row
    : { ...row, betrag: calculateVerkaufBetrag(row.menge, row.preis) }
));
const hasRowData = (row, wildDefaults) => {
  if (isPersisted(row)) return true;

  const defaultEinheit = valueOrEmpty(wildDefaults?.Std_Einheit);
  const defaultPreis = valueOrEmpty(wildDefaults?.Std_Preis);
  return Object.entries({
    ...row,
    markedForDeletion: undefined,
    bezahlt: undefined,
    datum: undefined
  }).some(([field, value]) => {
    if (field === "einheit" && value === defaultEinheit) return false;
    if (field === "preis" && value === defaultPreis) return false;
    return value !== undefined && value !== null && value !== "";
  });
};

export default function MehrfachVerkaufDialog({
  loading = false,
  open,
  onClose,
  onSave,
  kundenListe,
  initialVerkaeufe,
  wildDefaults,
  defaultDatum
}) {
  const defaultPreis = wildDefaults?.Std_Preis;
  const defaultEinheit = wildDefaults?.Std_Einheit;
  const stableWildDefaults = React.useMemo(() => ({
    Std_Preis: defaultPreis,
    Std_Einheit: defaultEinheit
  }), [defaultPreis, defaultEinheit]);
  const [zeilen, setZeilen] = useState([
    ...prepareInitialRows(initialVerkaeufe),
    createNewVerkaufRow(stableWildDefaults, defaultDatum)
  ]);
  const [saving, setSaving] = useState(false);
  const [dateEdited, setDateEdited] = useState(false);
  const [dateError, setDateError] = useState(false);
  const initialDatum = getInitialVerkaufDatum(initialVerkaeufe, defaultDatum);

  const [datum, setDatum] = useState(initialDatum);
  // const [verkaufsListe, setVerkaufsListe] = useState(initialVerkaeufe || []);

  // Immer eine leere Zeile unten anhängen, wenn die letzte Zeile ausgefüllt wird
  useEffect(() => {
    if (zeilen.length === 0) {
      setZeilen([createNewVerkaufRow(stableWildDefaults, defaultDatum)]);
      return;
    }
    const letzte = zeilen[zeilen.length - 1];
    if (hasRowData(letzte, stableWildDefaults)) {
      setZeilen([...zeilen, createNewVerkaufRow(stableWildDefaults, defaultDatum)]);
    }
  }, [zeilen, stableWildDefaults, defaultDatum]);

//  useEffect(() => {
//    setVerkaufsListe(initialVerkaeufe || []);
//  }, [initialVerkaeufe]);

  useEffect(() => {
    setZeilen([ ...prepareInitialRows(initialVerkaeufe), createNewVerkaufRow(stableWildDefaults, defaultDatum) ]);
  }, [initialVerkaeufe, open, stableWildDefaults, defaultDatum]);

  useEffect(() => {
    // Beim Öffnen: persistiertes Datum oder Zerlegedatum für neue Verkäufe.
    setDatum(getInitialVerkaufDatum(initialVerkaeufe, defaultDatum));
    setDateEdited(false);
    setDateError(false);
  }, [defaultDatum, initialVerkaeufe, open]);

  const handleChange = (idx, feld, wert) => {
    const kopie = zeilen.map((z, i) => {
      if (i !== idx) return z;
      const nextRow = { ...z, [feld]: wert };
      if (feld === "menge" || feld === "preis") {
        nextRow.betrag = calculateVerkaufBetrag(nextRow.menge, nextRow.preis);
      }
      return nextRow;
    });
    setZeilen(kopie);
  };

  const handleCheckbox = (idx, feld) => e => {
    const kopie = zeilen.map((z, i) => i === idx ? { ...z, [feld]: e.target.checked } : z);
    setZeilen(kopie);
  };

  const handleDelete = (idx) => {
    const row = zeilen[idx];
    if (isPersisted(row)) {
      setZeilen(zeilen.map((entry, index) => index === idx
        ? { ...entry, markedForDeletion: !entry.markedForDeletion }
        : entry));
    } else {
      setZeilen(zeilen.filter((_, index) => index !== idx));
    }
  };

  const handleSave = async () => {
    const activeRows = zeilen
      .filter(row => !row.markedForDeletion && hasRowData(row, stableWildDefaults))
      .map(({ markedForDeletion, ...row }) => ({
        ...row,
        menge: row.menge?.toString().replace(",", "."),
        datum: dateEdited ? normalizeDialogDate(datum) : normalizeVerkaufDatum(row.datum)
      }));
    if (activeRows.some(row => !row.datum)) {
      setDateError(true);
      document.getElementById("mehrfach-verkauf-datum")?.focus();
      return;
    }
    const deletedIds = zeilen
      .filter(row => row.markedForDeletion && isPersisted(row))
      .map(row => row.id);

    setSaving(true);
    try {
      await onSave({ activeRows, deletedIds });
      setZeilen([createNewVerkaufRow(stableWildDefaults, defaultDatum)]);
    } catch {
      // The parent keeps the dialog open and displays the operation error.
    } finally {
      setSaving(false);
    }
  };

  const total = zeilen
    .filter(row => !row.markedForDeletion)
    .reduce((sum, row) => {
      const value = Number.parseFloat(String(row.betrag ?? "").replace(",", "."));
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);

  const einheiten = Array.from(new Set([
    "kg",
    "Stk",
    valueOrEmpty(stableWildDefaults.Std_Einheit),
    ...zeilen.map(row => valueOrEmpty(row.einheit))
  ].filter(Boolean)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Mehrere Verkäufe erfassen</DialogTitle>
      <DialogContent>
        {loading && <LoadingIndicator label="Kunden und Verkäufe werden geladen …" />}
        {/* Datumsfeld über der Tabelle */}
        <DialogDateField
          label="Datum"
          id="mehrfach-verkauf-datum"
          type="date"
          value={datum}
          onChange={e => {
            setDatum(e.target.value);
            setDateEdited(true);
            setDateError(false);
          }}
          error={dateError}
          helperText={dateError ? "Verkaufsdatum ist erforderlich." : undefined}
          size="small"
          margin="dense"
        />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Kunde</TableCell>
              <TableCell>Menge</TableCell>
              <TableCell>Einheit</TableCell>
              <TableCell>Preis</TableCell>
              <TableCell>Betrag</TableCell>
              <TableCell>Bezahlt</TableCell>
              <TableCell>Bemerkung</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {zeilen.map((zeile, idx) => (
              <TableRow
                key={zeile.id ? `persisted-${zeile.id}` : `new-${idx}`}
                data-testid={isPersisted(zeile) ? `verkauf-row-${zeile.id}` : undefined}
                sx={zeile.markedForDeletion ? {
                  backgroundColor: red[50],
                  "&:hover": { backgroundColor: red[50] }
                } : undefined}
              >
                <TableCell>
                  <TextField
                    value={zeile.id || ""}
                    size="small"
                    margin="dense"
                    sx={{ width: { xs: "100%", sm: "9ch" } }}
                    disabled
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={zeile.kunde_id}
                    onChange={e => handleChange(idx, "kunde_id", e.target.value)}
                    displayEmpty
                    size="small"
                    margin="dense"
                    sx={{ minWidth: { xs: "16ch", sm: "22ch" } }}
                    disabled={!!zeile.markedForDeletion}
                  >
                    <MenuItem value=""><em>Bitte wählen</em></MenuItem>
                    {kundenListe.map(k =>
                      <MenuItem key={k.Id} value={k.Id}>
                        {personDisplayName(k)}
                      </MenuItem>
                    )}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    value={zeile.menge}
                    onChange={e => handleChange(idx, "menge", e.target.value)}
                    size="small"
                    margin="dense"
                    type="number"
                    inputProps={{ "aria-label": `Menge Zeile ${idx + 1}` }}
                    sx={{ width: { xs: "100%", sm: "10ch" } }}
                    disabled={!!zeile.markedForDeletion}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={zeile.einheit}
                    onChange={e => handleChange(idx, "einheit", e.target.value)}
                    size="small"
                    margin="dense"
                    sx={{ minWidth: { xs: "9ch", sm: "11ch" } }}
                    disabled={!!zeile.markedForDeletion}
                  >
                    {einheiten.map(einheit =>
                      <MenuItem key={einheit} value={einheit}>{einheit}</MenuItem>
                    )}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    value={zeile.preis}
                    onChange={e => handleChange(idx, "preis", e.target.value)}
                    size="small"
                    margin="dense"
                    type="number"
                    inputProps={{ "aria-label": `Preis Zeile ${idx + 1}` }}
                    sx={{ width: { xs: "100%", sm: "12ch" } }}
                    disabled={!!zeile.markedForDeletion}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={formatVerkaufBetrag(zeile.betrag)}
                    size="small"
                    margin="dense"
                    type="text"
                    InputProps={{ readOnly: true }}
                    inputProps={{ "aria-label": `Betrag Zeile ${idx + 1}` }}
                    sx={{ width: { xs: "100%", sm: "12ch" } }}
                    disabled={!!zeile.markedForDeletion}
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!zeile.bezahlt}
                        onChange={handleCheckbox(idx, "bezahlt")}
                        disabled={!!zeile.markedForDeletion}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={zeile.bemerkung}
                    onChange={e => handleChange(idx, "bemerkung", e.target.value)}
                    size="small"
                    margin="dense"
                    sx={{ minWidth: { xs: "16ch", sm: "22ch" } }}
                    disabled={!!zeile.markedForDeletion}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color={zeile.markedForDeletion ? "primary" : "error"}
                    aria-label={zeile.markedForDeletion
                      ? `Verkauf ${zeile.id} wiederherstellen`
                      : isPersisted(zeile) ? `Verkauf ${zeile.id} löschen` : "Neue Verkaufszeile löschen"}
                    title={zeile.markedForDeletion ? "Löschung rückgängig machen" : "Verkauf löschen"}
                    onClick={() => handleDelete(idx)}
                  >
                    {zeile.markedForDeletion ? <RestoreFromTrash /> : <Delete />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={5} />
              <TableCell sx={{ fontWeight: 700 }}>Gesamt: {total.toFixed(2)} €</TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          </TableFooter>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" startIcon={<Close />}>Abbrechen</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<Save />} disabled={saving}>Speichern</Button>
      </DialogActions>
    </Dialog>
  );
}
